import Table, {
  Row,
  TableHeading,
  TableAction,
  TableControls,
  TableFooter,
  TableRenderHelpers
} from "@/components/Table";
import TableDetailsPanel from "@/components/TableDetailsPanel";
import EmptyState from "@/components/EmptyState";
import { SortingState, VisibilityState, Table as TableComponent } from "@tanstack/react-table";
import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { DocumentNode, useQuery, useMutation } from "@apollo/client";
import { buildNotesTableColumns } from "./columns";
import { CRMNote, NoteNotable, NoteNotableValues, NotePriorityValues, NoteVisibilityValues } from "@/types";
import { formatNotePriority, formatNoteVisibility, getNotesPagePath, formatSourceName } from "./noteHelpers";
import { buildActionColumn } from "@/components/Table/buildActionColumn";
import useTableRowMenu from "@/hooks/useTableRowMenu";
import type { Action } from "@/components/MenuButton";
import useCapabilities, { useCanManageTasksBool, useCanViewTasksBool } from "@/hooks/useCapabilities";
import useCurrentBackofficeUser from "@/hooks/useCurrentBackofficeUser";
import { useFlags } from "@/hooks/useFeatureFlags";
import AddTaskSideSheet from "../AddTaskSideSheet/AddTaskSideSheet";
import type { BackofficeCRMReferenceableInput } from "../AddTaskSideSheet/mutations";
import NoteSideSheet from "../NoteSideSheet/NoteSideSheet";
import AddEditNoteSideSheet from "../AddEditNoteSideSheet/AddEditNoteSideSheet";
import { DELETE_NOTE_MUTATION, DeleteNoteResponse } from "./mutations";
import Dialog from "@/components/Dialog";
import Button from "@/components/Button";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import Banner from "@/components/Banner";
import useNotesTableFilters from "./useNotesTableFilters";
import usePagination from "@/hooks/usePagination";
import SearchInput from "@/components/SearchInput";
import Select from "@/components/Select";
import MultiSelect from "@/components/MultiSelect";
import DateSelect from "@/components/DateSelect";
import { DateValueType } from "react-tailwindcss-datepicker";
import { PageInfo } from "@/components/Pagination/Pagination";
import ReferencesTable from "../ReferencesTable";
import useAmplitude from "@/hooks/useAmplitude";
import { EVENT_ANALYTICS_EVENTS, getEventAnalyticsData } from "@/pages/events/_utils/amplitude";

type NotesTableVariant = "top-level" | "details-panel";

export interface NoteEditingConfig {
  enabled: boolean;
  notableId: string;
  notableType: NoteNotable;
  refetchQueries?: DocumentNode[];
  pgaId?: string;
  eventSlug?: string;
  objectType?: string;
}

interface NotesConnection {
  nodes: CRMNote[];
  totalCount: number;
  pageInfo: PageInfo;
}

type HideableFilter = "sourceType" | "visibility";

interface NotesTableProps {
  variant?: NotesTableVariant;
  notesQuery?: DocumentNode;
  queryVariables?: Record<string, unknown>;
  selectConnection?(data: unknown): NotesConnection | undefined;
  data?: CRMNote[];
  loading?: boolean;
  onRowClick?: (note: CRMNote) => void;
  renderControls?: (table: TableComponent<CRMNote>, helpers: TableRenderHelpers) => React.ReactElement | null;
  renderFooter?: () => React.ReactElement | null;
  tableHeading?: string;
  tableBody?: string;
  actions?: TableAction[];
  getParentLink?: (note: CRMNote) => string | null | undefined;
  getCopyLink?: (note: CRMNote) => string | null | undefined;
  title?: string;
  highlightNoteId?: string | null;
  openNoteId?: string | null;
  onOpenNoteChange?: (noteId: string | null) => void;
  noteEditing?: NoteEditingConfig;
  refetchQueries?: DocumentNode[];
  notableTypeFilter?: NoteNotable[];
  notableIdFilter?: string;
  hiddenFilters?: HideableFilter[];
  addNoteVariant?: TableAction["variant"];
  hideControls?: boolean;
}

const PRIORITY_OPTIONS = [
  { value: NotePriorityValues.NONE, label: formatNotePriority(NotePriorityValues.NONE) },
  { value: NotePriorityValues.PROMOTE, label: formatNotePriority(NotePriorityValues.PROMOTE) },
  { value: NotePriorityValues.ALERT, label: formatNotePriority(NotePriorityValues.ALERT) }
];

const VISIBILITY_OPTIONS = [
  { value: "all", displayText: "Show All" },
  { value: NoteVisibilityValues.INTERNAL, displayText: formatNoteVisibility(NoteVisibilityValues.INTERNAL) },
  { value: NoteVisibilityValues.EXTERNAL, displayText: formatNoteVisibility(NoteVisibilityValues.EXTERNAL) }
];

const SOURCE_TYPE_OPTIONS = Object.values(NoteNotableValues).map((value) => ({
  value,
  label: formatSourceName(value)
}));

function ConnectedNotesTable({
  notesQuery,
  queryVariables,
  selectConnection = (data: { personNotes?: NotesConnection } | undefined) => data?.personNotes,
  notableTypeFilter,
  notableIdFilter,
  hiddenFilters,
  ...rest
}: Omit<NotesTableProps, "data" | "loading" | "renderControls" | "renderFooter"> & {
  notesQuery: DocumentNode;
}) {
  const hideControls = rest.hideControls ?? false;
  const { id: currentUserId } = useCapabilities();
  const filters = useNotesTableFilters({ currentUserId });
  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination();

  const [sortingState, setSortingState] = useState<SortingState>([{ id: "updatedAt", desc: true }]);

  const sourceTypeOptions = useMemo(() => {
    if (notableTypeFilter && notableTypeFilter.length > 0) {
      return SOURCE_TYPE_OPTIONS.filter((opt) => notableTypeFilter.includes(opt.value as NoteNotable));
    }
    return SOURCE_TYPE_OPTIONS;
  }, [notableTypeFilter]);

  const orderByVariable = filters.buildOrderByVariable(sortingState);

  const resolvedNotableType = useMemo(() => {
    if (filters.sourceTypeFilter.length > 0) return filters.sourceTypeFilter;
    if (notableTypeFilter && notableTypeFilter.length > 0) return notableTypeFilter;
    return undefined;
  }, [filters.sourceTypeFilter, notableTypeFilter]);

  const inputVariable = useMemo(() => {
    const base = { ...filters.inputVariables };
    if (resolvedNotableType) {
      base.notableType = resolvedNotableType;
    }
    if (notableIdFilter) {
      base.notableId = notableIdFilter;
    }
    return Object.keys(base).length > 0 ? base : undefined;
  }, [filters.inputVariables, resolvedNotableType, notableIdFilter]);

  const prevInputRef = useRef(inputVariable);
  const prevSortRef = useRef(orderByVariable);

  useEffect(() => {
    const inputChanged = JSON.stringify(prevInputRef.current) !== JSON.stringify(inputVariable);
    const sortChanged = JSON.stringify(prevSortRef.current) !== JSON.stringify(orderByVariable);

    if (inputChanged || sortChanged) {
      resetPagination();
    }

    prevInputRef.current = inputVariable;
    prevSortRef.current = orderByVariable;
  }, [inputVariable, orderByVariable, resetPagination]);

  const { data: queryData, loading } = useQuery(notesQuery, {
    variables: {
      ...queryVariables,
      ...(hideControls ? {} : paginationVariables),
      input: inputVariable,
      orderBy: orderByVariable
    },
    errorPolicy: "all"
  });

  const connection = selectConnection(queryData);
  const data = connection?.nodes || [];

  const handleSortingChange = (updaterOrValue: any) => {
    const sortState = typeof updaterOrValue === "function" ? updaterOrValue(sortingState) : updaterOrValue;
    setSortingState(sortState);
  };

  const renderControls = (_table: TableComponent<CRMNote>, { onSortClick }: TableRenderHelpers) => (
    <TableControls
      pagination={createHeaderPagination(connection)}
      onSortClick={onSortClick}
      useExpandedFilters={true}
      expandedFiltersState={filters.filterState}
      onClearAllFilters={() => {
        filters.handleClearAllFilters();
        resetPagination();
      }}
      activeFilters={filters.activeFilterCount}
      filterChips={[
        {
          label: "Show My Notes",
          onClick: () => filters.setShowMyNotes(!filters.showMyNotes),
          selected: filters.showMyNotes
        }
      ]}
    >
      <SearchInput
        placeholder="Search Notes"
        query={filters.searchValue}
        onChange={filters.setSearchValue}
        onClear={filters.clearSearch}
      />
      {!hiddenFilters?.includes("sourceType") && sourceTypeOptions.length > 1 && (
        <div data-name="sourceType" data-filter-size="custom">
          <MultiSelect
            label="Source Type"
            options={sourceTypeOptions}
            selectedValues={filters.sourceTypeFilter}
            onSelect={(value, checked) => {
              const updated = checked
                ? [...filters.sourceTypeFilter, value]
                : filters.sourceTypeFilter.filter((v) => v !== value);
              filters.setSourceTypeFilter(updated);
            }}
            onClear={() => filters.setSourceTypeFilter([])}
          />
        </div>
      )}
      <div data-name="priority">
        <MultiSelect
          label="Priority"
          options={PRIORITY_OPTIONS}
          selectedValues={filters.priorityFilter}
          onSelect={(value, checked) => {
            const updated = checked
              ? [...filters.priorityFilter, value]
              : filters.priorityFilter.filter((v) => v !== value);
            filters.setPriorityFilter(updated);
          }}
          onClear={() => filters.setPriorityFilter([])}
        />
      </div>
      {!hiddenFilters?.includes("visibility") && (
        <div data-name="visibility">
          <Select
            label="Visibility"
            options={VISIBILITY_OPTIONS}
            value={filters.visibilityFilter}
            onSelect={filters.setVisibilityFilter}
          />
        </div>
      )}
      <div data-name="dateRange" data-filter-size="custom">
        <DateSelect
          label="Date Range"
          value={filters.dateRange}
          onChange={(val) => filters.setDateRange(val as DateValueType)}
          placeholder="Show All"
          useModal
          useDefaultShortcuts
        />
      </div>
    </TableControls>
  );

  const renderFooter = () => <TableFooter pagination={createFooterPagination(connection, { loading })} />;

  const hasActiveFilters = filters.activeFilterCount > 0 || !!filters.searchValue || filters.showMyNotes;

  const mergedRefetchQueries = useMemo(() => {
    const base = rest.refetchQueries ?? rest.noteEditing?.refetchQueries ?? [];
    return base.includes(notesQuery) ? base : [notesQuery, ...base];
  }, [rest.refetchQueries, rest.noteEditing?.refetchQueries, notesQuery]);

  return (
    <NotesTableBase
      {...rest}
      data={data}
      loading={loading}
      renderControls={renderControls}
      renderFooter={renderFooter}
      sortingState={sortingState}
      onSortingChange={handleSortingChange}
      serverSideSorting={true}
      hasActiveFilters={hasActiveFilters}
      refetchQueries={mergedRefetchQueries}
    />
  );
}

interface NotesTableBaseProps extends Omit<
  NotesTableProps,
  "notesQuery" | "queryVariables" | "notableTypeFilter" | "notableIdFilter"
> {
  sortingState?: SortingState;
  onSortingChange?: (updaterOrValue: any) => void;
  serverSideSorting?: boolean;
  hasActiveFilters?: boolean;
  addNoteVariant?: TableAction["variant"];
}

function resolveRefetchQueries(
  refetchQueries: DocumentNode[] | undefined,
  noteEditing: NoteEditingConfig | undefined
): DocumentNode[] | undefined {
  return refetchQueries ?? noteEditing?.refetchQueries;
}

function NotesTableBase({
  variant = "top-level",
  data: externalData,
  loading = false,
  onRowClick,
  renderControls: externalRenderControls,
  renderFooter: externalRenderFooter,
  tableHeading,
  tableBody,
  actions = [],
  getParentLink,
  getCopyLink,
  title,
  highlightNoteId,
  openNoteId,
  onOpenNoteChange,
  noteEditing,
  refetchQueries: refetchQueriesProp,
  sortingState: externalSortingState,
  onSortingChange: externalOnSortingChange,
  serverSideSorting = false,
  hasActiveFilters = false,
  addNoteVariant = "outlined",
  hideControls = false
}: NotesTableBaseProps) {
  const [internalSortingState, setInternalSortingState] = useState<SortingState>([{ id: "updatedAt", desc: true }]);
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);
  const [isSideSheetOpen, setIsSideSheetOpen] = useState(false);

  const [isAddEditOpen, setIsAddEditOpen] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [noteToDelete, setNoteToDelete] = useState<CRMNote | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [addTaskOpen, setAddTaskOpen] = useState(false);
  const [addTaskReferences, setAddTaskReferences] = useState<BackofficeCRMReferenceableInput[]>([]);

  const navigate = useNavigate();
  const location = useLocation();
  const { Menu, setMenu } = useTableRowMenu();
  const { id: currentUserId } = useCapabilities();
  const currentUser = useCurrentBackofficeUser();
  const canViewTasks = useCanViewTasksBool();
  const canManageTasks = useCanManageTasksBool();
  const flags = useFlags();
  const referencesEnabled = !!flags["backoffice-person-note-references"];
  const showAddTask = canManageTasks;

  const editingEnabled = noteEditing?.enabled ?? false;
  const refetchQueries = resolveRefetchQueries(refetchQueriesProp, noteEditing);
  const { trackEvent } = useAmplitude();

  const [deleteNote, { loading: deletingNote }] = useMutation<DeleteNoteResponse>(DELETE_NOTE_MUTATION, {
    refetchQueries
  });

  const sortingState = externalSortingState ?? internalSortingState;
  const handleSortingChange =
    externalOnSortingChange ??
    ((updaterOrValue: any) => {
      const sortState = typeof updaterOrValue === "function" ? updaterOrValue(sortingState) : updaterOrValue;
      setInternalSortingState(sortState);
    });

  const data = externalData || [];

  const hasScrolledToHighlight = useRef(false);
  const prevHighlightNoteId = useRef(highlightNoteId);
  if (prevHighlightNoteId.current !== highlightNoteId) {
    prevHighlightNoteId.current = highlightNoteId;
    hasScrolledToHighlight.current = false;
  }
  useEffect(() => {
    if (!highlightNoteId || hasScrolledToHighlight.current || loading || !data.length) return;
    hasScrolledToHighlight.current = true;

    let raf: number;
    const timeout = setTimeout(() => {
      raf = requestAnimationFrame(() => {
        const row = document.querySelector("tr.row-highlight") as HTMLElement | null;
        if (row) {
          row.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
    }, 100);
    return () => {
      clearTimeout(timeout);
      cancelAnimationFrame(raf);
    };
  }, [highlightNoteId, loading, data.length]);

  const handleOpenSideSheet = (noteId: string) => {
    setSelectedNoteId(noteId);
    setIsSideSheetOpen(true);
    onOpenNoteChange?.(noteId);
  };

  useEffect(() => {
    if (loading) return;
    if (openNoteId && openNoteId !== selectedNoteId) {
      setSelectedNoteId(openNoteId);
      setIsSideSheetOpen(true);
    } else if (!openNoteId && isSideSheetOpen && selectedNoteId) {
      setIsSideSheetOpen(false);
      setSelectedNoteId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openNoteId, loading]);

  const handleCloseSideSheet = () => {
    setIsSideSheetOpen(false);
    setSelectedNoteId(null);
    onOpenNoteChange?.(null);
  };

  const handleEditFromSideSheet = () => {
    if (selectedNoteId) {
      setIsSideSheetOpen(false);
      setEditingNoteId(selectedNoteId);
      setIsAddEditOpen(true);
      onOpenNoteChange?.(null);
    }
  };

  const handleAddTaskFromSideSheet = () => {
    if (selectedNoteId) {
      setIsSideSheetOpen(false);
      setAddTaskReferences([{ referenceableId: selectedNoteId, referenceableType: "NOTE" }]);
      onOpenNoteChange?.(null);
      setAddTaskOpen(true);
    }
  };

  const handleOpenAddNote = () => {
    setEditingNoteId(null);
    setIsAddEditOpen(true);
  };

  const handleOpenEditNote = (noteId: string) => {
    setEditingNoteId(noteId);
    setIsAddEditOpen(true);
  };

  const handleCloseAddEdit = () => {
    setIsAddEditOpen(false);
    setEditingNoteId(null);
  };

  const handleCopyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      showSnackbar("Link copied to clipboard");
    } catch {
      showErrorSnackbar("Failed to copy link to clipboard");
    }
  };

  const handleSaveSuccess = () => {
    setIsAddEditOpen(false);
    setEditingNoteId(null);
  };

  const handleDeleteConfirm = async () => {
    if (!noteToDelete) return;
    setDeleteError(null);
    try {
      const result = await deleteNote({ variables: { noteId: noteToDelete.id } });
      if (result.data?.deleteNote?.success) {
        showSnackbar("Note deleted");
        if (noteEditing?.objectType === "event" || noteEditing?.notableType === NoteNotableValues.EVENT) {
          trackEvent(
            EVENT_ANALYTICS_EVENTS.noteDeleted,
            getEventAnalyticsData({
              source: "notes",
              eventId: noteEditing.notableId,
              eventSlug: noteEditing.eventSlug,
              noteId: noteToDelete.id,
              action: "delete"
            })
          );
        }
        setNoteToDelete(null);
      } else {
        setDeleteError(result.data?.deleteNote?.message || "Failed to delete note");
      }
    } catch {
      setDeleteError("Failed to delete note");
    }
  };

  const canDeleteNote = (note: CRMNote) =>
    editingEnabled && note.visibility === NoteVisibilityValues.INTERNAL && note.author?.id === currentUserId;

  const handleRowClick = (row: Row<CRMNote>) => {
    handleOpenSideSheet(row.original.id);
    onRowClick?.(row.original);
  };

  const selectedNote = data.find((n) => n.id === selectedNoteId);
  const parentLink = selectedNote && getParentLink ? getParentLink(selectedNote) : null;
  const parentLinkPathname = parentLink?.split("?")[0];
  const isParentCurrentPage = !!(parentLinkPathname && location.pathname === parentLinkPathname);
  const handleParentClick = parentLink && !isParentCurrentPage ? () => navigate(parentLink) : undefined;

  const viewAllNotesPath = useMemo(() => {
    const pgaId = noteEditing?.pgaId;
    if (!pgaId) return undefined;
    const path = getNotesPagePath(pgaId, location.pathname);
    if (location.pathname === path) return undefined;
    return path;
  }, [noteEditing?.pgaId, location.pathname]);

  const handleViewAllNotes = viewAllNotesPath
    ? () => {
        const url = selectedNoteId ? `${viewAllNotesPath}?highlightNote=${selectedNoteId}` : viewAllNotesPath;
        navigate(url);
      }
    : undefined;

  const resolvedActions: TableAction[] = useMemo(() => {
    if (!editingEnabled) return actions;

    const addNoteAction: TableAction = {
      label: "Add Note",
      icon: "add",
      variant: addNoteVariant,
      onClick: handleOpenAddNote,
      testId: "add-note-button"
    };

    return [addNoteAction, ...actions];
  }, [editingEnabled, actions, addNoteVariant]);

  const columns = buildNotesTableColumns(variant, getParentLink, {
    referencesEnabled,
    navigate,
    currentPathname: location.pathname
  });

  columns.push(
    buildActionColumn<CRMNote>({
      getActions: (note: CRMNote) => {
        const actions: Action[] = [];
        if (editingEnabled) {
          actions.push({
            section: "Manage",
            text: "Edit Note",
            onClick: () => handleOpenEditNote(note.id)
          });
        }
        if (showAddTask) {
          actions.push({
            section: "Actions",
            text: "Add Task",
            onClick: () => {
              setAddTaskReferences([{ referenceableId: note.id, referenceableType: "NOTE" }]);
              setAddTaskOpen(true);
            }
          });
        }
        if (getCopyLink) {
          const url = getCopyLink(note);
          if (url) {
            actions.push({
              section: "Actions",
              text: "Copy Link",
              copyFieldLabel: "note link",
              copyFieldId: note.id,
              onClick: () => {
                void handleCopyLink(url);
              },
              menuOnly: true
            });
          }
        }
        if (editingEnabled && canDeleteNote(note)) {
          actions.push({
            text: "Delete Note",
            iconName: "delete",
            destructive: true,
            onClick: () => setNoteToDelete(note)
          });
        }
        return actions;
      },
      onViewClick: (note: CRMNote) => {
        handleOpenSideSheet(note.id);
      },
      showViewButton: () => true,
      viewButtonTitle: "View Note Details",
      actionId: (rowId: string) => `action-${rowId}`,
      setMenu,
      getBadgeCount: (note: CRMNote) => (canViewTasks ? (note.openTasksCount ?? 0) : 0)
    })
  );

  const handleCloseDeleteDialog = () => {
    setNoteToDelete(null);
    setDeleteError(null);
  };

  const deleteNoteDialog = noteToDelete && (
    <Dialog
      open={true}
      onClosed={handleCloseDeleteDialog}
      headline="Delete Note"
      className="w-[490px] p-6 pr-2"
      actions={
        <>
          <Button variant="text" onClick={handleCloseDeleteDialog} disabled={deletingNote}>
            Cancel
          </Button>
          <Button
            variant="filled"
            theme="error"
            trailingIcon={false}
            icon="delete"
            onClick={handleDeleteConfirm}
            disabled={deletingNote}
            testId="confirm-delete-note"
          >
            Delete
          </Button>
        </>
      }
    >
      {deleteError && <Banner variant="error" description={deleteError} inline closable={false} hideSecondaryDismiss />}
      <p>Are you sure you want to delete this note? This cannot be undone.</p>
    </Dialog>
  );

  const columnVisibility: VisibilityState =
    variant === "details-panel"
      ? {
          source: false
        }
      : {};

  if (variant === "details-panel") {
    return (
      <>
        <TableDetailsPanel
          data={data}
          columns={columns}
          loading={loading}
          onClickRow={handleRowClick}
          title={title}
          sortingState={sortingState}
          onSortingChange={handleSortingChange}
          getRowClasses={
            highlightNoteId ? (row) => (row.original.id === highlightNoteId ? "row-highlight" : "") : undefined
          }
        />
        <NoteSideSheet
          open={isSideSheetOpen}
          onClose={handleCloseSideSheet}
          noteId={selectedNoteId}
          onParentClick={handleParentClick}
          showSource={false}
          onEditNote={editingEnabled ? handleEditFromSideSheet : undefined}
          onAddTask={showAddTask ? handleAddTaskFromSideSheet : undefined}
          onViewAllNotes={handleViewAllNotes}
          getCopyLink={getCopyLink}
        />
        <Menu />
        {deleteNoteDialog}
        {noteEditing && (
          <AddEditNoteSideSheet
            open={isAddEditOpen}
            onClose={handleCloseAddEdit}
            noteId={editingNoteId}
            notableId={noteEditing.notableId}
            notableType={noteEditing.notableType}
            refetchQueries={refetchQueries}
            currentUserId={currentUserId || undefined}
            currentUser={currentUser}
            pgaId={noteEditing.pgaId}
            eventSlug={noteEditing.eventSlug}
            onSaveSuccess={handleSaveSuccess}
            objectType={noteEditing.objectType}
          />
        )}
        {showAddTask && (
          <AddTaskSideSheet
            open={addTaskOpen}
            onClose={() => setAddTaskOpen(false)}
            currentUser={currentUser}
            defaultReferences={addTaskReferences}
            refetchQueries={refetchQueries}
          />
        )}
      </>
    );
  }

  return (
    <>
      {(tableHeading || tableBody || resolvedActions.length > 0) && (
        <TableHeading heading={tableHeading} body={tableBody} actions={resolvedActions} />
      )}
      <Table
        data={data}
        columns={columns}
        loading={loading}
        onClickRow={handleRowClick}
        renderControls={hideControls ? undefined : externalRenderControls}
        renderFooter={hideControls ? () => <TableFooter totalRowCount={data.length} /> : externalRenderFooter}
        columnVisibility={columnVisibility}
        sortingState={sortingState}
        onSortingChange={handleSortingChange}
        serverSideSorting={serverSideSorting}
        hasActiveFilters={hasActiveFilters}
        data-testid="notes-table"
        renderNoDataEmptyState={() => (
          <EmptyState
            iconName="sticky_note_2"
            title="No Notes Yet"
            caption="There are no notes to display at this time."
          />
        )}
        getRowCanExpand={referencesEnabled ? (row) => (row.original.references?.length || 0) > 0 : undefined}
        renderExpandedRow={
          referencesEnabled
            ? ({ row }) => (
                <ReferencesTable
                  variant="details-panel"
                  references={row.original.references || []}
                  title={`References (${row.original.references?.length || 0})`}
                />
              )
            : undefined
        }
        getRowClasses={
          highlightNoteId ? (row) => (row.original.id === highlightNoteId ? "row-highlight" : "") : undefined
        }
      />
      <NoteSideSheet
        open={isSideSheetOpen}
        onClose={handleCloseSideSheet}
        noteId={selectedNoteId}
        onParentClick={handleParentClick}
        showSource={!isParentCurrentPage}
        onEditNote={editingEnabled ? handleEditFromSideSheet : undefined}
        onAddTask={showAddTask ? handleAddTaskFromSideSheet : undefined}
        onViewAllNotes={handleViewAllNotes}
        getCopyLink={getCopyLink}
      />
      <Menu />
      {deleteNoteDialog}
      {noteEditing && (
        <AddEditNoteSideSheet
          open={isAddEditOpen}
          onClose={handleCloseAddEdit}
          noteId={editingNoteId}
          notableId={noteEditing.notableId}
          notableType={noteEditing.notableType}
          refetchQueries={refetchQueries}
          currentUserId={currentUserId || undefined}
          currentUser={currentUser}
          pgaId={noteEditing.pgaId}
          eventSlug={noteEditing.eventSlug}
          onSaveSuccess={handleSaveSuccess}
          objectType={noteEditing.objectType}
        />
      )}
      {showAddTask && (
        <AddTaskSideSheet
          open={addTaskOpen}
          onClose={() => setAddTaskOpen(false)}
          currentUser={currentUser}
          defaultReferences={addTaskReferences}
          refetchQueries={refetchQueries}
        />
      )}
    </>
  );
}

export default function NotesTable({ notesQuery, queryVariables, ...rest }: NotesTableProps) {
  if (notesQuery) {
    return <ConnectedNotesTable notesQuery={notesQuery} queryVariables={queryVariables} {...rest} />;
  }

  return <NotesTableBase {...rest} />;
}
